import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import StepIndicator from "../../components/StepIndicator";
import VoiceSelector from "../../components/VoiceSelector";

type Step = "brand" | "campaign" | "generate";

const STEP_ORDER: Step[] = ["brand", "campaign", "generate"];
const STEP_LABELS = [
  "Create your brand",
  "Set up a campaign",
  "Generate first batch",
];

const VOICES = [
  "professional",
  "friendly",
  "bold",
  "witty",
  "educational",
  "inspirational",
];
const PLATFORMS = [
  { id: "instagram_feed", label: "Instagram" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "X / Twitter" },
  { id: "facebook", label: "Facebook" },
];

interface Props {
  onComplete: (brandId: string) => void;
}

export default function Onboarding({ onComplete }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("brand");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Brand form state
  const [brandName, setBrandName] = useState("");
  const [voice, setVoice] = useState("friendly");
  const [industry, setIndustry] = useState("");
  const [audience, setAudience] = useState("");
  const [brandId, setBrandId] = useState<string | null>(null);

  // Campaign form state
  const [campaignName, setCampaignName] = useState("");
  const [product, setProduct] = useState("");
  const [platforms, setPlatforms] = useState<string[]>([
    "instagram_feed",
    "linkedin",
  ]);
  const [campaignId, setCampaignId] = useState<string | null>(null);

  // Generation preferences
  const [postVariationsPerPlatform, setPostVariationsPerPlatform] = useState(1);

  const togglePlatform = (id: string) =>
    setPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  const handleCreateBrand = async () => {
    if (!brandName.trim()) return setError("Brand name is required");
    setSaving(true);
    setError(null);
    try {
      const { brand } = await api.brands.create({
        name: brandName,
        voice: voice as never,
        industry,
        targetAudience: audience,
      });
      setBrandId(brand.brandId);
      setStep("campaign");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim() || !brandId)
      return setError("Campaign name is required");
    if (!platforms.length) return setError("Select at least one platform");
    setSaving(true);
    setError(null);
    try {
      const { campaign } = await api.campaigns.create(brandId, {
        name: campaignName,
        platforms: platforms as never,
        product: { name: product, description: "", differentiators: [] },
      });
      setCampaignId(campaign.campaignId);
      setStep("generate");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!brandId || !campaignId) return;
    setSaving(true);
    setError(null);
    try {
      // Save the variations preference to the user profile first
      await api.me.patchProfile({ postVariationsPerPlatform }).catch(() => {
        /* non-fatal — preference will be saved next time user visits Settings */
      });
      await api.generate.dispatch({
        brandId,
        campaignId,
        platforms,
        variationsCount: postVariationsPerPlatform,
      });
      // Signal App.tsx that brand creation + campaign are done, then go to
      // social setup so the user can connect accounts for this brand.
      onComplete(brandId);
      navigate(`/setup/social?brandId=${brandId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex justify-center pt-4">
      <div className="w-full max-w-[800px]">
        {/* Steps indicator */}
        <StepIndicator
          steps={STEP_LABELS}
          current={STEP_ORDER.indexOf(step)}
          className="mb-8"
        />

        <div className="card px-11 py-10">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 mb-4">
              {error}
            </p>
          )}

          {/* Step 1: Brand */}
          {step === "brand" && (
            <div className="space-y-6">
              <h2 className="text-3xl font-black text-ink">
                Tell us about your brand
              </h2>
              <div>
                <label className="label !text-base">
                  Brand name <span className="text-spark">*</span>
                </label>
                <input
                  className="input text-base py-3"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Acme Coffee Co."
                />
              </div>
              <div>
                <label className="label !text-base">Industry</label>
                <input
                  className="input text-base py-3"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="Food & Beverage"
                />
              </div>
              <div>
                <label className="label !text-base">Target audience</label>
                <input
                  className="input text-base py-3"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="Coffee lovers aged 25–45, urban professionals"
                />
              </div>
              <div>
                <label className="label !text-base">Brand voice</label>
                <VoiceSelector
                  voices={VOICES}
                  value={voice}
                  onChange={setVoice}
                />
              </div>
              <button
                onClick={handleCreateBrand}
                disabled={saving}
                className="btn-primary w-full mt-2 text-base py-3.5"
              >
                {saving ? "Saving…" : "Continue →"}
              </button>
            </div>
          )}

          {/* Step 2: Campaign */}
          {step === "campaign" && (
            <div className="space-y-6">
              <h2 className="text-3xl font-black text-ink">
                Create your first campaign
              </h2>
              <div>
                <label className="label !text-base">
                  Campaign name <span className="text-spark">*</span>
                </label>
                <input
                  className="input text-base py-3"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Spring Launch 2026"
                />
              </div>
              <div>
                <label className="label !text-base">
                  What are you promoting?
                </label>
                <input
                  className="input text-base py-3"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="Single-origin espresso subscription"
                />
              </div>
              <div>
                <label className="label !text-base">Platforms</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      className={`px-5 py-2.5 rounded-full text-[15px] font-bold border transition-colors ${platforms.includes(p.id) ? "bg-brand-500 text-white border-brand-500" : "border-gray-300 text-slate hover:border-brand-400"}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleCreateCampaign}
                disabled={saving}
                className="btn-primary w-full mt-2 text-base py-3.5"
              >
                {saving ? "Saving…" : "Continue →"}
              </button>
            </div>
          )}

          {/* Step 3: Generate */}
          {step === "generate" && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="text-5xl mb-2">✨</div>
                <h2 className="text-3xl font-black text-ink">
                  You're set up. Let's make content.
                </h2>
                <p className="text-sm text-muted mt-1">
                  Postza will generate your first batch of posts using Gemini.
                  You'll land in the Review Queue to approve, edit, or reject
                  each one.
                </p>
              </div>

              {/* Variations preference */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-sm font-medium text-ink">
                    How many variations per platform?
                  </p>
                  <p className="text-xs text-muted mt-0.5">
                    Each variation uses a different hook, angle, or tone. More
                    variations means more to choose from — and more quota used.
                  </p>
                </div>
                <div className="flex gap-2">
                  {([1, 2, 3] as const).map((n) => (
                    <button
                      key={n}
                      onClick={() => setPostVariationsPerPlatform(n)}
                      aria-pressed={postVariationsPerPlatform === n}
                      className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                        postVariationsPerPlatform === n
                          ? "bg-brand-500 text-white border-brand-500"
                          : "border-gray-300 text-slate hover:border-brand-400 bg-white"
                      }`}
                    >
                      {n === 1
                        ? "1 — Standard"
                        : n === 2
                          ? "2 — More choice"
                          : "3 — Maximum"}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-faint">
                  {postVariationsPerPlatform === 1
                    ? `Generates ${platforms.length} post${platforms.length !== 1 ? "s" : ""} (1 per platform).`
                    : `Generates ${platforms.length * postVariationsPerPlatform} posts (${postVariationsPerPlatform} per platform). You can change this later in Settings.`}
                </p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={saving}
                className="btn-primary w-full"
              >
                {saving ? "Generating…" : "Generate first batch"}
              </button>
              <button
                onClick={() => {
                  if (!brandId) return;
                  onComplete(brandId);
                  navigate(`/setup/social?brandId=${brandId}`);
                }}
                className="btn-secondary w-full"
              >
                Skip — I'll do it manually
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
